/**
 * HTML Sanitization Utility
 *
 * NOTE: This is a basic sanitizer. For production, install and use DOMPurify:
 * ```
 * pnpm add dompurify @types/dompurify
 * import DOMPurify from 'dompurify'
 * ```
 *
 * Basic sanitization removes potentially dangerous HTML while
 * preserving safe formatting tags.
 */

const ALLOWED_TAGS = [
  'p',
  'a',
  'strong',
  'em',
  'ul',
  'li',
  'ol',
  'div',
  'span',
  'br',
  'hr',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'td',
  'th',
  'pre',
  'code',
  'blockquote',
  'button',
  'input',
]
const ALLOWED_ATTRS = [
  'href',
  'target',
  'rel',
  'data-action',
  'data-scope',
  'data-kb-slug',
  'data-issue-type',
  'data-action-id',
  'data-params',
  'data-context-key',
  'data-value',
  'data-use-input',
  'class',
  'id',
  'style',
  'type',
  'placeholder',
]

/**
 * Basic HTML sanitizer
 * Removes script tags, event handlers, and dangerous attributes
 *
 * @param html - HTML string to sanitize
 * @returns Sanitized HTML string
 */
export function sanitizeHtml(html: string): string {
  if (!html) return ''

  // Remove script tags and content
  let sanitized = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')

  // Remove event handlers (onclick, onerror, etc.)
  sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '')
  sanitized = sanitized.replace(/\s*on\w+\s*=\s*[^\s>]*/gi, '')

  // Remove javascript: protocol
  sanitized = sanitized.replace(/javascript:/gi, '')

  // Remove data: protocol (can be used for XSS)
  sanitized = sanitized.replace(/data:text\/html/gi, '')

  // Basic tag filtering - remove any tags not in allowed list
  // This is a simple implementation; DOMPurify is more robust
  const tagRegex = /<\/?([a-z][a-z0-9]*)\b[^>]*>/gi
  sanitized = sanitized.replace(tagRegex, (match, tagName) => {
    if (ALLOWED_TAGS.includes(tagName.toLowerCase())) {
      return match
    }
    return ''
  })

  return sanitized
}

/**
 * Sanitize HTML attributes
 * Only allows safe attributes like href, target, rel
 *
 * @param html - HTML string to sanitize
 * @returns HTML string with only allowed attributes
 */
export function sanitizeAttributes(html: string): string {
  if (!html) return ''

  // This is a basic implementation
  // For production, use DOMPurify which handles edge cases better
  return html.replace(/<([a-z][a-z0-9]*)\s+([^>]*)>/gi, (match, tagName, attrs) => {
    const sanitizedAttrs = attrs.replace(
      /(\w+)\s*=\s*["']([^"']*)["']/g,
      (attrMatch: string, attrName: string, attrValue: string) => {
        if (ALLOWED_ATTRS.includes(attrName.toLowerCase())) {
          return `${attrName}="${attrValue}"`
        }
        return ''
      }
    )
    return `<${tagName} ${sanitizedAttrs}>`
  })
}

/**
 * TODO: Replace with DOMPurify when Node version is updated
 *
 * Comprehensive HTML sanitization using DOMPurify
 * Install: pnpm add dompurify @types/dompurify
 *
 * Example implementation:
 * ```typescript
 * import DOMPurify from 'dompurify'
 *
 * export function sanitizeHtmlWithDOMPurify(html: string): string {
 *   return DOMPurify.sanitize(html, {
 *     ALLOWED_TAGS: ['p', 'a', 'strong', 'em', 'ul', 'li', 'div', 'br'],
 *     ALLOWED_ATTR: ['href', 'target', 'rel', 'data-action'],
 *     ALLOW_DATA_ATTR: false,
 *   })
 * }
 * ```
 */
